import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchTranslation,
  getUserName,
  getUserHashId,
  getTenant,
  getTenantIsInternal,
  getMenus,
  getUserError,
  toggleDebugTranslation,
  getDebugTranslation,
  updateError,
  getShowReleaseNotes,
  getLastSeenVersion,
  toggleHelpEditMode,
  toogleSeedRequiredDataMode,
  getHelpEditMode,
  getIsDirty,
  setIsDirty,
  getSessionTimeout,
  getTranslation,
  toggleShowLkBadge,
  getShowLkBadge,
  getModule,
  resetModule,
  getImpersonatedBy,
} from "store/userSlice";

import { ReactComponent as Logo } from "assets/images/peb-logo.svg";
import infoIcon from "assets/images/icons/info.png";
import calendarWhiteIcon from "assets/images/icons/calendar-white.png";
import buildingWhiteIcon from "assets/images/icons/building-white.png";
import fileIcon from "assets/images/icons/file.png";
import calendarIcon from "assets/images/icons/calendar.png";
import settingsIcon from "assets/images/icons/settings.png";
import userSettingIcon from "assets/images/icons/user-setting.png";
import logoutIcon from "assets/images/icons/logout.png";
import { fetchBalanceScorecardData, setOverrideReadOnly } from "store/balanceScorecardSlice";
import { setCalculationOverrideReadOnly } from "store/calculationSettingsSlice";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.min.css";
import * as Sentry from "@sentry/react";
import ReleaseNotes from "components/ui/ReleaseNotes";
import { holdReadyFlag, releaseReadyFlag } from "helpers/readyFlag";
import { api } from "helpers/axios";
import { errorMessage, formatDateTime, leaveConfirm } from "helpers/common";
import { useForm } from "react-hook-form";
import { doLogout } from "helpers/logout";
import { useIdleTimer } from "react-idle-timer";
import { isUserCookie, removeCsrfCookie } from "helpers/cookie";
import { fetchPeriods, getActivePeriod, getPeriods } from "store/periodSlice";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { fetchGeneralSettings } from "store/generalSettingsSlice";
import navbarEmployeeManagement from "assets/images/icons/navbar-employee-management.png";
import navbarCalculation from "assets/images/icons/navbar-calculation.png";
import navbarStatsReports from "assets/images/icons/navbar-stats-reports.png";
import navbarSettings from "assets/images/icons/navbar-settings.png";
import navbarEvaluator from "assets/images/icons/navbar-evaluator.png";
import navbarReviewer from "assets/images/icons/navbar-reviewer.png";
import navbarTeamObjective from "assets/images/icons/navbar-team-objectives.png";
import ErrorAlert from "components/ui/ErrorAlert";
import { useTranslation } from "translation/useTranslation";
import Link from "components/ui/Link";
import FullPageLoader from "components/ui/FullPageLoader";
import ScenarioImportDebugMenu from "components/BalanceScorecard/ScenarioImportDebugMenu";
import { isDbModule, moduleToBadgeClass, moduleToFallback, moduleToTranslationKey } from "helpers/module";
import { moduleOf, sortPeriodsForDropdown } from "helpers/periods";
import { Button } from "components/ui";
import ImpersonationBanner from "components/Layout/ImpersonationBanner";

const navbarIcons = {
  '/employee-management': navbarEmployeeManagement,
  '/evaluation-statistics': navbarEvaluator,
  '/calculation': navbarCalculation,
  '/stats-reports': navbarStatsReports,
  '/settings': navbarSettings,
  '/evaluation/evaluator-overview': navbarEvaluator,
  '/evaluation/secondary-evaluator-overview': navbarEvaluator,
  '/evaluation/review': navbarReviewer,
  '/evaluation/all-evaluations': navbarReviewer,
  '/evaluation/team-objectives-overview': navbarTeamObjective,
};

const environmentLabels = {
  LOCAL: {
    label: 'LOCAL Umgebung',
    translation_key: 'global.environment.header.local'
  },
  DEV: {
    label: 'DEV Umgebung',
    translation_key: 'global.environment.header.dev'
  },
  UAT: {
    label: 'UAT Umgebung',
    translation_key: 'global.environment.header.uat'
  },
  DEMO: {
    label: 'DEMO Umgebung',
    translation_key: 'global.environment.header.demo'
  },
};

export default function UserLayout({ children, testId = null }) {
  const location = useLocation();
  const dispatch = useDispatch();

  const { translate, editableTranslate } = useTranslation();

  const currentModule = useSelector(getModule);

  const isDbModuleActive = useMemo(() => {
    return isDbModule(currentModule);
  }, [currentModule]);

  const userTranslation = useSelector(getTranslation);
  const debugTranslation = useSelector(getDebugTranslation);
  const helpEditMode = useSelector(getHelpEditMode);
  const showLkBadge = useSelector(getShowLkBadge);
  const userName = useSelector(getUserName);
  const impersonatedBy = useSelector(getImpersonatedBy);
  const userHashId = useSelector(getUserHashId);
  const tenant = useSelector(getTenant);
  const isInternalTenant = useSelector(getTenantIsInternal);
  const showReleaseNotes = useSelector(getShowReleaseNotes);
  const lastseenVersion = useSelector(getLastSeenVersion);
  const menus = useSelector(getMenus);
  const error = useSelector(getUserError);
  const periods = useSelector(getPeriods);
  const activePeriod = useSelector(getActivePeriod);
  const sessionTimeout = useSelector(getSessionTimeout);

  const sortedPeriods = useMemo(() => sortPeriodsForDropdown(periods), [periods]);

  const globalIsDirty = useSelector(getIsDirty);

  const currentPath = useMemo(() => {
    return location.pathname.replace(/^\/(?:state|db|standard|lob)(?=\/|$)/, '');
  }, [location.pathname]);

  // Global settings are neither module- nor period-scoped: the screens under /system-settings
  // read across every module and every period, and initial setup runs before either module means
  // anything. Both drop the module badge, the module-scoped main navigation and the module accent,
  // and carry the original PEB red instead. The routes are untouched - they stay addressable, so
  // a test still reaches them with a plain goto.
  const isSystemSettings = useMemo(() => currentPath.startsWith('/system-settings'), [currentPath]);

  const headerEnvironmentLabel = useMemo(() => {
    const environment = (process.env.REACT_APP_ENV || '').trim().toUpperCase();
    return environmentLabels[environment] || null;
  }, []);

  const [releaseNotes, setReleaseNotes] = useState(null);
  const [showTranslationEditor, setShowTranslationEditor] = useState(false);
  const [translationKey, setTranslationKey] = useState(null);
  const [switchingPeriod, setSwitchingPeriod] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [translationChangelog, setTranslationChangelog] = useState(null);
  const [loadingTranslationChangelog, setLoadingTranslationChangelog] = useState(false);
  const [loadingTranslation, setLoadingTranslation] = useState(false);

  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const [hjl, setHjl] = useState(typeof window !== 'undefined' && typeof window.hj !== 'undefined');
  const [hjWsConnected, setHjWsConnected] = useState(false);

  const [sessionState, setSessionState] = useState('active');
  const [sessionRemaining, setSessionRemaining] = useState(0);

  const navRef = useRef(null);

  const translationWarning = {
    'global': '<h5 class="alert-heading">You are editing a translation with <code>global.*</code> prefix.</h5> <p>This translation is used in multiple contexts throughout the application. Changing this will affect all occurrences of this translation.</p>',
    'field': '<h5 class="alert-heading">You are editing a translation with <code>field.*</code> prefix.</h5> <p>This translation is used when we displaying the field label particularly in forms, input fields, table headers, reports and more. Changing this will affect all occurrences of this translation.</p>',
    'button': '<h5 class="alert-heading">You are editing a translation with <code>button.*</code> prefix.</h5> <p>This translation is used for button labels throughout the application. Changing this will affect all occurrences of this translation.</p>',
  }

  // Observe the nav itself so --nav-height also follows content changes (e.g. the impersonation strip), not only window resizes.
  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const updateNavHeight = () => document.documentElement.style.setProperty('--nav-height', `${nav.offsetHeight}px`);
    updateNavHeight();
    const observer = new ResizeObserver(updateNavHeight);
    observer.observe(nav);
    return () => observer.disconnect();
  }, []);

  const { register, getValues, reset } = useForm();

  const isEvaluation = useMemo(() => {
    return currentPath.startsWith('/evaluation/');
  }, [currentPath]);

  const isInitialSetup = useMemo(() => {
    return currentPath.startsWith('/initial-setup');
  }, [currentPath]);

  const isModuleless = isSystemSettings || isInitialSetup;

  // Global settings carry their own navigation in the navbar rather than in a sidebar,
  // so the items come from the same /system-settings submenu tree the sidebar read.
  const systemSettingsMenus = useMemo(() => {
    if (!isSystemSettings || !menus) return [];
    const parent = menus.find((menu) => menu.path === '/system-settings');
    return parent?.submenus?.data || [];
  }, [isSystemSettings, menus]);

  const [canAccessEvaluation, evaluationSubMenus] = useMemo(() => {
    if (!menus) return [false, null];

    const filtered = menus.filter(m => m.path === '/evaluation');

    if (filtered.length < 1) return [false, null];

    const differentTranslations = {
      '/evaluation/evaluator-overview': 'state_employee.initial_evaluation',
      '/evaluation/secondary-evaluator-overview': 'state_employee.secondary_evaluation',
    };

    const subMenus = [...filtered[0].submenus.data].map(m => {
      let translationKey = m.translation_key;
      if (isDbModuleActive && differentTranslations.hasOwnProperty(m.path)) {
        translationKey = differentTranslations[m.path];
      }

      return {
        ...m,
        translation_key: translationKey
      };
    });

    return [true, subMenus];
  }, [menus, isDbModuleActive]);

  const canAccessEmployeeManagement = useMemo(() => {
    if (menus) {
      const filtered = menus?.filter(m => m.path === '/employee-management');
      return filtered.length > 0;
    }
    return false;
  }, [menus]);

  const canAccessSystemSettings = useMemo(() => {
    if (menus) {
      const filtered = menus?.filter(m => m.path === '/system-settings');
      return filtered.length > 0;
    }
    return false;
  }, [menus]);

  useEffect(() => {
    dispatch(fetchTranslation());

    if (!periods) {
      dispatch(fetchPeriods());
    }

    dispatch(fetchGeneralSettings());
  }, []);

  useEffect(() => {
    if (showReleaseNotes && lastseenVersion && searchParams.get('with') === 'changelogs') {
      // window.history.replaceState({}, '');
      // remove from searchParams from URL
      setSearchParams({});

      api.get(`/release-notes/${lastseenVersion}`)
        .then(({ data }) => {
          setReleaseNotes(data);
        })
        .catch(err => {
          Sentry.captureException(err);
        });
    }
  }, [showReleaseNotes]);

  // check if .editable-translation is clicked
  useEffect(() => {
    if (!debugTranslation) return;

    const findInSections = (tx, key, lang) => {
      const order = ['global', 'dropdowns', 'tooltips'];
      for (const sec of order) {
        const val = tx?.[sec]?.[key]?.[lang];
        if (val != null && val !== '') return val;
      }
      return key; // fallback
    };

    const getLangsForKey = (tx, key, langs = ['en', 'de']) => {
      const out = {};
      for (const l of langs) out[l] = findInSections(tx, key, l);
      return out;
    };

    const listener = async (e) => {
      if (!(e.target instanceof Element)) return;

      const editableTarget = e.target.closest('.editable-translation');
      if (editableTarget) {
        e.preventDefault();
        setLoadingTranslation(true);
        setShowTranslationEditor(true);

        const key = editableTarget.getAttribute('data-key');

        setTranslationKey(key);
        setTranslationChangelog(null);

        try {
          // search from global, then tooltips then dropdowns
          const translations = await dispatch(fetchTranslation(true)).unwrap();
          reset(getLangsForKey(translations, key, ['en', 'de']));
        } catch (err) {
          reset(getLangsForKey(null, key, ['en', 'de']));
          Sentry.captureException(err);
        } finally {
          setLoadingTranslation(false);
        }
      }
    }

    document.addEventListener('click', listener);

    return () => {
      document.removeEventListener('click', listener);
    }
  }, [userTranslation, debugTranslation]);

  const onIdle = () => {
    setSessionState('idle');

    setShowLogoutModal(true);

    setTimeout(() => {
      doLogout(dispatch, 'Session timeout due to inactivity', false);
    }, 5000);
  };

  const onAction = () => {
    if (!isUserCookie() && !showLogoutModal) onIdle();
  };

  const {
    getRemainingTime,
    getTabId,
    isLeader,
    isLastActiveTab
  } = useIdleTimer({
    onIdle,
    onAction,
    timeout: sessionTimeout * 60 * 1000, // convert minutes to milliseconds
    crossTab: true,
    stopOnIdle: true,
    syncTimers: 200,
    leaderElection: true
  });

  useEffect(() => {
    // do nothing if not isInternalTenant and not local environment
    if (!(tenant && isInternalTenant) && process.env.REACT_APP_ENV !== 'local') {
      return;
    }

    const interval = setInterval(() => {
      // convert milliseconds to seconds
      setSessionRemaining(Math.ceil(getRemainingTime() / 1000));
    }, 500);

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflowY = (showLogoutModal ? 'hidden' : 'auto');
  }, [showLogoutModal]);

  useEffect(() => {
    if (!translationKey || !showTranslationEditor) return;

    getTranslationChangelog();

  }, [translationKey, showTranslationEditor]);

  const handleSaveTranslation = async () => {
    const values = getValues();

    try {
      const { status } = await api.post(`/common/translations`, {
        key: translationKey,
        en: values.en,
        de: values.de
      });

      if (status === 200) {
        setShowTranslationEditor(false);
        toast.success(translate('form.success.saved'));
        handleReloadTranslation();
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  }

  const getTranslationChangelog = async () => {
    setLoadingTranslationChangelog(true);
    try {
      // make this url_encode
      let key = encodeURIComponent(translationKey);
      const { data } = await api.get(`/common/translations/changelog?key=${key}`);
      setTranslationChangelog(data);
    } catch (err) {
      Sentry.captureException(err);
    } finally {
      setLoadingTranslationChangelog(false);
    }
  };

  const handleLogout = async (e) => {
    e?.preventDefault();

    if (globalIsDirty) {
      const confirmed = await leaveConfirm();
      if (!confirmed) return;
      else dispatch(setIsDirty(false));
    }

    doLogout(dispatch);
  };

  const handleReloadTranslation = async (e) => {
    e?.preventDefault();
    dispatch(fetchTranslation(true));
  };

  const handleToggleTranslation = async (e) => {
    e.preventDefault();
    dispatch(toggleDebugTranslation());
    handleReloadTranslation(e);
  };

  const handleToggleHelpEditMode = async (e) => {
    e.preventDefault();
    dispatch(toggleHelpEditMode());
  };

  const handleToggleDisplayLkBadge = async (e) => {
    e.preventDefault();
    dispatch(toggleShowLkBadge());
  };

  const handleToggleSeedRequiredData = async (e) => {
    e.preventDefault();
    dispatch(toogleSeedRequiredDataMode());
  };

  const handlePrefillData = (e, dataType) => {
    e.preventDefault();

    if (dataType === 'reset') {
      window.location.reload();
      return;
    }

    dispatch(fetchBalanceScorecardData(dataType));
  };

  const handleOverrideBalanceScorecardReadonly = (e) => {
    e.preventDefault();
    dispatch(setOverrideReadOnly(true));
  };

  const handleOverrideCalculationReadonly = (e) => {
    e.preventDefault();
    dispatch(setCalculationOverrideReadOnly(true));
  };

  const handleChangePeriod = async (e, period) => {
    e.preventDefault();

    // do nothing if period is already active
    if (period === activePeriod) return;

    if (globalIsDirty) {
      const confirmed = await leaveConfirm();
      if (!confirmed) return;
      else dispatch(setIsDirty(false));
    }

    setSwitchingPeriod(true);
    holdReadyFlag();
    const currentPeriod = activePeriod;

    try {
      const { data, status } = await api.post(`/common/periods/init-switch`, {
        period: period
      });

      if (status === 200) {
        try {
          // reset cookie and set new period
          removeCsrfCookie();
          localStorage.setItem('period', data.data.p);
          api.defaults.headers['X-Period'] = data.data.p;
          const { status } = await api.post(`/common/periods/switch`, {
            uc: data.data.uc,
          });

          if (status === 200) {
            dispatch(resetModule());
            window.location.reload();
          }
        } catch (err) {
          localStorage.setItem('period', currentPeriod);
          throw err;
        }
      }
    } catch (err) {
      dispatch(updateError(errorMessage(err, 'switch period')));
      Sentry.captureException(err);
      releaseReadyFlag();
    } finally {
      setSwitchingPeriod(false);
    }
  };

  if (!userName) return <></>;

  return (
    <>
      <FullPageLoader show={switchingPeriod} translation={{ key: 'global.loading.switching_period', default: 'Switching period...' }} />
      <div className={`${helpEditMode ? 'help-edit-mode' : ''} ${isModuleless ? 'global-module-global' : (isDbModuleActive ? 'global-module-db' : 'global-module-lob')}`}>
        {/* evaluation pages = minimum tablet, else = minimum desktop */}
        <div className={(isEvaluation && currentPath !== '/evaluation/team-objectives-overview') ? 'd-md-none d-sm-block' : 'd-md-block d-lg-none'}>
          <div className="d-flex justify-content-center align-items-center vh-100 p-5">
            <h4 className="text-break">{editableTranslate(isEvaluation ? 'global.minimum_tablet_warning' : 'global.minimum_desktop_warning')}</h4>
          </div>
        </div>
        <div className={`modal logout-modal ${showLogoutModal ? 'fade show d-block' : ''}`} tabIndex="-1" data-testid="logout-modal">
          <div className="modal-dialog modal-dialog-centered modal-sm">
            <div className="modal-content">
              <div className="modal-body">
                <div className="text-center">
                  <i className="bi bi-door-closed"></i>
                  <div>{editableTranslate('logout.modal_text')}</div>
                  <Button as={Link} variant="primary" className="mt-2" to="/login" reloadDocument={true} data-testid="confirm-button">{editableTranslate('button.ok')}</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        {
          showLogoutModal && <div className="modal-backdrop logout-modal-backdrop fade show"></div>
        }
        <div className={`${(isEvaluation && currentPath !== '/evaluation/team-objectives-overview') ? 'd-none d-sm-none d-md-block' : 'd-none d-md-none d-lg-block'} ${debugTranslation ? 'debug-translation' : ''}`}>
          <nav ref={navRef} id="navbar-menu" data-testid="navbar" className={`navbar navbar-expand navbar-dark ${impersonatedBy ? 'flex-wrap' : 'flex-nowrap'} px-4 py-2 ` + (isModuleless ? 'module-global' : (isDbModuleActive ? 'module-db' : 'module-lob'))}>
            <ImpersonationBanner className="impersonation-strip" />
            <div className="navbar-brand">
              <Link data-testid="home-link" to={'/'}>
                <Logo className="img-logo" />
              </Link>
              {
                !isModuleless && <div className="module-indicator ml-1-25">{editableTranslate(moduleToTranslationKey(currentModule), moduleToFallback(currentModule))}</div>
              }
            </div>
            <button className="navbar-toggler" type="button" data-toggle="collapse" data-target="#navbarSupportedContent" aria-controls="navbarSupportedContent" aria-expanded="false" aria-label="Toggle navigation" data-testid="nav-toggle">
              <span className="navbar-toggler-icon"></span>
            </button>

            <div className="collapse navbar-collapse" id="navbarSupportedContent">
              {
                isSystemSettings && (
                  <div className="navbar-section-title" data-testid="nav-section-title">
                    {editableTranslate('global.menu.system_settings_title', 'Modulübergreifende Einstellungen')}
                  </div>
                )
              }
              <ul className="navbar-nav left-side" data-testid="nav-primary">
                {
                  isSystemSettings && systemSettingsMenus.map((menu, i) => {
                    const navKey = (menu.path || '').split('/').filter(Boolean).pop();
                    return (
                      <li key={i} className={`nav-item ${currentPath.startsWith(menu.path) ? 'active' : ''}`} data-testid={`nav-item-${navKey}`}>
                        <Link to={menu.path} className="nav-link"><i className={`${menu.icon} pr-2`}></i>{editableTranslate(menu.translation_key, menu.display_name)}</Link>
                      </li>
                    );
                  })
                }
                {
                  (!isEvaluation && !isInitialSetup && !isSystemSettings && menus) && menus.map((menu, i) => {
                    const navKey = (menu.path || '').replace(/^\//, '');
                    if (!['/evaluation', '/help', '/system-settings'].includes(menu.path)) return (
                      <Fragment key={i}>
                        <li className={`nav-item ${currentPath.startsWith(menu.path) ? 'active' : ''}`} data-testid={`nav-item-${navKey}`}>
                          <img src={navbarIcons[menu.path]} />
                          <Link data-testid="nav-link" to={menu.path} className={`nav-link`}>{editableTranslate(menu.translation_key, menu.display_name)}</Link>
                        </li>
                      </Fragment>
                    )
                    return null
                  })
                }
                {
                  (isEvaluation && !isInitialSetup && evaluationSubMenus) && evaluationSubMenus.map((menu, i) => {
                    const navKey = (menu.path || '').replace(/^\//, '').replace(/\//g, '-');
                    return <li key={i} className={`nav-item ${currentPath.startsWith(menu.path) ? 'active' : ''}`} data-testid={`nav-item-${navKey}`}>
                      <img src={navbarIcons[menu.path]} />
                      <Link data-testid="nav-link" to={menu.path} className={`nav-link`}>{editableTranslate(menu.translation_key)}</Link>
                    </li>
                  })
                }
              </ul>
              <ul className="navbar-nav right-side" data-testid="nav-secondary">
                {
                  headerEnvironmentLabel && (
                    <li className="nav-item navbar-environment-slot">
                      <span className="navbar-environment-label">
                        {editableTranslate(headerEnvironmentLabel.translation_key, headerEnvironmentLabel.label)}
                      </span>
                    </li>
                  )
                }
                {
                  menus && <>
                    <li className={`nav-item ${currentPath.startsWith('/help') ? 'active' : ''}`} data-testid="nav-item-help">
                      <Link to={'/help'} className={`nav-link px-0`}>
                        <span className="nav-help">
                          <i className="bi bi-question"></i>
                        </span>
                      </Link>
                    </li>
                    {!isSystemSettings && <li className="nav-item nav-divider"></li>}
                  </>
                }
                {
                  isSystemSettings ? (
                    // Global settings are a place you leave rather than work from, so the
                    // tenant and period control gives way to a close button. Everything it
                    // held (period switch, Profil, Abmelden) is one click away again at /.
                    <li className="nav-item">
                      <Link to="/" className="nav-link px-0" data-testid="close-settings" aria-label={translate('global.menu.close_settings', 'Einstellungen schliessen')}>
                        <span className="nav-close">
                          <i className="bi bi-x-lg"></i>
                        </span>
                      </Link>
                    </li>
                  ) : (
                <li className="nav-item active dropdown">
                  <span className="nav-link username-link" href="/#" role="button" data-toggle="dropdown" aria-expanded="false" data-testid="user-menu-trigger">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <div>
                          <img src={buildingWhiteIcon} className="building-icon mr-3" />
                          <span className="v-middle">{tenant.substr(0, 20) + (tenant.length > 20 ? ' ..' : '')}</span>
                        </div>
                        <div>
                          <img src={calendarWhiteIcon} className="calendar-icon mr-3" />
                          {
                            activePeriod && <span className="v-middle">{activePeriod.substr(0, 20) + (activePeriod.length > 20 ? ' ..' : '')}</span>
                          }
                        </div>
                      </div>
                      <i className="bi bi-chevron-down bi-bold pl-3 pr-1"></i>
                    </div>
                  </span>
                  <div className="dropdown-menu dropdown-menu-up dropdown-menu-user" data-testid="user-menu">
                    {
                      canAccessEvaluation && <>
                        {
                          (isEvaluation && canAccessEmployeeManagement) && <Link data-testid="menu-admin-view" to="/employee-management" className={`dropdown-item`}><img src={fileIcon} className="mr-2" />{editableTranslate('global.menu.admin_view')}</Link>
                        }
                        {
                          (!isEvaluation || !canAccessEmployeeManagement) && <Link data-testid="menu-evaluator-view" to="/evaluation" className={`dropdown-item ${currentPath.startsWith('/evaluation/') ? 'active' : ''}`}><img src={fileIcon} className="mr-2" />{isDbModuleActive ? editableTranslate('global.menu.evaluator_view.db', 'Beurteilungsmodul') : editableTranslate('global.menu.evaluator_view.lob', 'Bewertungsmodul')}</Link>
                        }
                      </>
                    }

                    <a className="dropdown-item period-dropdown-toggle" href="/#" role="button" data-toggle="dropdown" data-testid="period-menu-trigger">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <img src={calendarIcon} className="mr-2" />
                          {
                            activePeriod && <span>{translate('period.period')} ({activePeriod.substr(0, 20) + (activePeriod.length > 20 ? ' ..' : '')})</span>
                          }
                        </div>
                        <i className={`bi bi-chevron-right bi-bold ${(activePeriod && activePeriod.length > 20) || tenant.length > 20 ? 'ml-2' : 'ml-2rem'}`}></i>
                      </div>
                    </a>

                    <div className="dropdown-menu period-dropdown-menu" data-testid="period-menu">
                      {
                        sortedPeriods.length > 0 && sortedPeriods.map((period) => <a key={period.name} className={`dropdown-item ${activePeriod === period.name ? 'active' : ''} ${!period.ready ? 'disabled' : ''}`} href="/#" onClick={(e) => handleChangePeriod(e, period.name)} data-testid={`period-option-${period.name}`}>
                          <span className="period-span">
                            {
                              moduleOf(period) && <div className={`badge-module ${moduleToBadgeClass(moduleOf(period))}`}>{editableTranslate(moduleToTranslationKey(moduleOf(period)), moduleToFallback(moduleOf(period)))}</div>
                            }
                            <span>{period.name}</span>
                            {
                              // animate loading icon
                              !period.ready && <span className="spinner-border spinner-border-sm ml-2" role="status" aria-hidden="true"></span>
                            }
                          </span>
                        </a>
                        )
                      }
                      {/* <div className="dropdown-divider"></div>
                      <Link className="dropdown-item" to="/system-settings/period-management">
                        <span className="new-period-span">
                          <i className="bi bi-calendar mr-2"></i>
                          {editableTranslate('period_management.manage_period', 'Manage period')}
                        </span>
                      </Link> */}
                    </div>
                    {
                      (menus && canAccessSystemSettings) && <Link to="/system-settings" className={`dropdown-item ${currentPath.startsWith('/system-settings') ? 'active' : ''}`} data-testid="menu-system-settings"><img src={settingsIcon} className="mr-2" />{editableTranslate('global.menu.system_settings', 'System Settings')}</Link>
                    }

                    <Link to="/profile-settings" className={`dropdown-item ${currentPath.startsWith('/profile-settings') ? 'active' : ''}`} data-testid="menu-profile-settings"><img src={userSettingIcon} className="mr-2" />{editableTranslate('global.profile_settings', 'Profile Settings')}</Link>
                    <Link to="/about" className={`dropdown-item ${currentPath.startsWith('/about') ? 'active' : ''}`} data-testid="menu-about"><img src={infoIcon} style={{ filter: 'brightness(0)' }} className="mr-2" />{editableTranslate('global.about', 'About')}</Link>

                    {
                      ((tenant && isInternalTenant) || process.env.REACT_APP_ENV === 'local') && <>
                        <div className="dropdown-divider"></div>

                        <div className="dropdown internal-tools-parent">
                        <a className="dropdown-item internal-tools-toggle" href="/#" role="button" onClick={(e) => e.preventDefault()}>
                          <div className="d-flex align-items-center justify-content-between">
                            <div>
                              <i className="bi bi-tools mr-2"></i>
                              <span>God mode</span>
                            </div>
                            <i className="bi bi-chevron-right bi-bold ml-2"></i>
                          </div>
                        </a>

                        {/* God mode only renders for internal tenants and locally, and its session
                            readouts tick every 500ms. Excluded from the ready flag's DOM
                            quiet check, which otherwise never settles on the local stack. */}
                        <div className="dropdown-menu internal-tools-menu" data-ready-ignore>
                          <span className="menu-section d-block ml-2 mt-1 font-weight-bold"><i className="bi bi-box-arrow-up-right"></i> Links</span>
                          <a className="dropdown-item" href={process.env.REACT_APP_ADMIN_BASE_URL} target="_blank">Admin portal </a>

                          <span className="menu-section d-block ml-2 mt-1 font-weight-bold"><i className="bi bi-translate"></i> Translation / wording</span>
                          <a className="dropdown-item" href="/#" onClick={handleToggleTranslation}>{debugTranslation ? 'Disable' : 'Enable'} edit mode</a>
                          <a className="dropdown-item" href="/#" onClick={handleReloadTranslation}>Reload</a>

                          <span className="menu-section d-block ml-2 mt-1 font-weight-bold"><i className="bi bi-question-circle-fill"></i> Inline help editor</span>
                          <a className="dropdown-item" href="/#" onClick={handleToggleHelpEditMode}>{helpEditMode ? 'Disable' : 'Enable'} edit mode</a>

                          <span className="menu-section d-block ml-2 mt-1 font-weight-bold"><i className="bi bi-person"></i> Employee management</span>
                          <a className="dropdown-item" href="/#" onClick={handleToggleDisplayLkBadge}>{showLkBadge ? 'Hide' : 'Show'} LK badge</a>
                          <ScenarioImportDebugMenu tenantIdentifier={tenant} activePeriod={activePeriod} />

                          {
                            !isEvaluation && <>
                              <span className="menu-section d-block ml-2 mt-1 font-weight-bold"><i className="bi bi-calculator-fill"></i> Calculation</span>
                              <Link to="/calculation/calculation-debugging" className="dropdown-item">Test helpers</Link>
                              <Link to="/calculation/calculation-test" className="dropdown-item">Test engine</Link>
                              <a className="dropdown-item" href="/#" onClick={handleToggleSeedRequiredData}>Toggle seed data</a>
                            </>
                          }

                          {
                            currentPath.includes('/settings/balance-scorecard/form') && <>
                              <span className="menu-section d-block ml-2 mt-1 font-weight-bold"><i className="bi bi-person-square"></i> Balanced score card</span>
                              <a className="dropdown-item" href="/#" onClick={(e) => handlePrefillData(e, 'individual_only')}>Individual only</a>
                              <a className="dropdown-item" href="/#" onClick={(e) => handlePrefillData(e, 'systematic_only')}>Systematic only</a>
                              <a className="dropdown-item" href="/#" onClick={(e) => handlePrefillData(e, 'individual_and_systematic')}>Both</a>
                              <a className="dropdown-item" href="/#" onClick={(e) => handlePrefillData(e, 'reset')}>Reset</a>
                              <a className="dropdown-item" href="/#" onClick={handleOverrideBalanceScorecardReadonly}>Override Readonly</a>
                            </>
                          }
                          {
                            currentPath.includes('/settings/calculation/form') && <>
                              <span className="menu-section d-block ml-2 mt-1 font-weight-bold"><i className="bi bi-calculator-fill"></i> Calculation settings</span>
                              <a className="dropdown-item" href="/#" onClick={handleOverrideCalculationReadonly}>Override Readonly</a>
                            </>
                          }
                          <hr />
                          {/* show hjl status */}
                          <span className="menu-section d-block ml-2 mt-1 font-weight-bold"><i className="bi bi-fire"></i> Hotjar</span>
                          <div className="dropdown-item">
                            {hjl ?
                              <span className="text-success"><i className="bi bi-check-circle"></i> Script loaded</span> :
                              <span className="text-danger"><i className="bi bi-x-circle"></i> Script not loaded</span>
                            }
                          </div>
                          <div className="dropdown-item">
                            {hjWsConnected ?
                              <span className="text-success"><i className="bi bi-camera-reels-fill"></i> Recording (likely)</span> :
                              <span className="text-danger"><i className="bi bi-x-circle"></i> Not recording</span>
                            }
                          </div>
                          <hr />
                          <span className="menu-section d-block ml-2 mt-1 font-weight-bold"><i className="bi bi-cup-hot-fill"></i> Session</span>
                          <div className="dropdown-item">
                            {sessionState === 'active' ?
                              <span className="text-success"><i className="bi bi-check-circle"></i> Active</span> :
                              <span className="text-danger"><i className="bi bi-x-circle"></i> Inactive</span>
                            }
                          </div>
                          <div className="dropdown-item">
                            <span>Tab ID: {getTabId() ? getTabId().toString() : 'loading'}</span>
                          </div>
                          <div className="dropdown-item">
                            <span>Is Leader: {isLeader() !== null ? isLeader().toString() : 'loading'}</span>
                          </div>
                          <div className="dropdown-item">
                            <span>Last Active Tab: {isLastActiveTab() !== null ? isLastActiveTab().toString() : 'loading'}</span>
                          </div>
                          <div className="dropdown-item">
                            <span>Remaining time: {sessionRemaining} seconds</span>
                          </div>
                        </div>
                        </div>
                      </>
                    }

                    <div className="dropdown-divider"></div>

                    <a id="logout" className="dropdown-item" href="/#" onClick={handleLogout} data-testid="logout-button"><img src={logoutIcon} className="mr-2" />{editableTranslate('global.logout', 'Ausloggen')}</a>
                  </div>
                </li>
                  )
                }
              </ul>
            </div>
          </nav>

          <div className="px-4">
            <div className="div-content pb-5" data-testid={testId}>
              {
                error && <div>
                  <ErrorAlert error={error} onClose={() => dispatch(updateError(null))} />
                </div>
              }

              {children}
            </div>
          </div>

          <span id="uid" className="d-none">{userHashId}</span>
          <span id="tenant" className="d-none">{tenant}</span>

          {
            ((tenant && isInternalTenant) || process.env.REACT_APP_ENV === 'local') &&
              <ConditionalHotjar userHashId={userHashId} tenant={tenant} setHjl={setHjl} setHjWsConnected={setHjWsConnected} />
          }

          {
            releaseNotes && <ReleaseNotes notes={releaseNotes} />
          }

          {
            debugTranslation && <>
              <div className={`modal release-notes-modal ${showTranslationEditor && 'fade show d-block'}`} tabIndex="-1" data-testid="translation-editor">
                <div className="modal-dialog modal-dialog-centered modal-lg">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Translation Editor</h5>
                    </div>
                    <div className="modal-body">
                      {
                        loadingTranslation && <h5 className="text-center"><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Fetching latest translation...</h5>
                      }
                      {
                        !loadingTranslation && translationKey && <>
                          {
                            Object.keys(translationWarning).map((prefix) => {
                              if (translationKey.startsWith(prefix)) {
                                return (
                                  <div className="alert alert-warning pb-0" role="alert" key={prefix}>
                                    <span dangerouslySetInnerHTML={{ __html: translationWarning[prefix] }} />
                                  </div>
                                );
                              }
                              return null;
                            })
                          }
                          <div className="form-group">
                            <span>Translation for key: <b>{translationKey}</b></span>
                          </div>
                          <div className="form-group">
                            <div className="row">
                              <div className="col-12">
                                <label className="d-block"><b>English</b></label>
                                <textarea type="text" className="form-control" autoComplete="off" {...register('en')} data-testid="en-input" />
                              </div>
                              <div className="col-12 mt-2">
                                <label className="d-block"><b>German</b></label>
                                <textarea type="text" className="form-control" autoComplete="off" {...register('de')} data-testid="de-input" />
                              </div>
                            </div>
                          </div>
                          <div className="form-group mt-3">
                            <label className="d-block"><b>Translation Changelog (Last 5 changes)</b></label>
                            {
                              loadingTranslationChangelog && !translationChangelog && <span className="d-block"><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Fetching Changelog...</span>
                            }
                            {
                              !loadingTranslationChangelog && (!translationChangelog || translationChangelog.length < 1) && <label className="d-block">No changelog found.</label>
                            }
                            {
                              translationChangelog && translationChangelog.length > 0 && (
                                <div className="table-responsive">
                                  <table className="table table-sm table-striped" data-testid="translation-changelog-table">
                                    <thead>
                                      <tr>
                                        <th>User</th>
                                        <th>Timestamp</th>
                                        <th>Old Value</th>
                                        <th>New Value</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {translationChangelog.map((item, index) => {
                                        const langs = Object.keys(item.old_value || {}).concat(Object.keys(item.new_value || {}));
                                        const uniqueLangs = [...new Set(langs)]; // avoid duplicates

                                        return (
                                          <tr key={index}>
                                            <td>{item.causer_identifier}</td>
                                            <td>{formatDateTime(item.created_at)}</td>
                                            <td>
                                              {uniqueLangs.map(lang =>
                                                item.old_value?.[lang] ? (
                                                  <div key={lang}>
                                                    <b>{lang}:</b> {item.old_value[lang]}
                                                  </div>
                                                ) : null
                                              )}
                                            </td>
                                            <td>
                                              {uniqueLangs.map(lang =>
                                                item.new_value?.[lang] ? (
                                                  <div key={lang}>
                                                    <b>{lang}:</b> {item.new_value[lang]}
                                                  </div>
                                                ) : null
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )
                            }
                          </div>
                        </>
                      }
                    </div>
                    {
                      !loadingTranslation && translationKey && <>
                        <div className="modal-footer">
                          <Button variant="secondary" onClick={() => setShowTranslationEditor(false)} data-testid="close-button">Close</Button>
                          <Button variant="primary" onClick={() => handleSaveTranslation()} data-testid="save-button">Save</Button>
                        </div>
                      </>
                    }
                  </div>
                </div>
              </div>
              {
                showTranslationEditor && <div className="modal-backdrop fade show"></div>
              }
            </>
          }
        </div>

        <div data-testid="toasts">
          <ToastContainer autoClose={3000} pauseOnFocusLoss={false} limit={1} />
        </div>

        <ReactTooltip id="reviewer-tooltip" content={translate('tooltips.employee.group.reviewer_tooltip', 'Sie haben Überprüfungsrechte für diese Gruppe.')} />
        <ReactTooltip id="evaluator-tooltip" content={translate('tooltips.employee.group.evaluator_tooltip', 'Sie haben Bewertungsrechte für diese Gruppe.')} />
        <ReactTooltip id="no-budget-tooltip" place="bottom" content={translate('tooltips.employee.balanced_scorecard.no_budget_tooltip', 'Eine Leistungskarte ohne Budget kann nicht berechnet werden.')} />
        <ReactTooltip id="no-group-tooltip" place="bottom" content={translate('tooltips.employee.balanced_scorecard.no_group_tooltip', 'Für eine Leistungskarte ohne Gruppe ist niemand zur Beurteilung zugewiesen.')} />
        <ReactTooltip id="no-group-db-tooltip" place="bottom" content={translate('tooltips.employee.balanced_scorecard.no_group_db_tooltip', 'Eine Beurteilung ohne Gruppe hat niemanden zur Beurteilung zugewiesen.')} />
        <ReactTooltip id="example-employee-tooltip" content={translate('tooltips.employee.example_employee_tooltip', 'Dieser Beispiel-Nutzer wurde für Sie zum einfachen Testen angelegt.')} />
        <ReactTooltip id="no-interview-tooltip" content={translate('tooltips.employee.no_interview', 'Sie haben noch keine Mitarbeitergespräche definiert.')} />
        <ReactTooltip id="bs-balanced_scorecard.tag.individual_and_systematic-tooltip" content={translate('tooltips.employee.bs-balanced_scorecard.tag.individual_and_systematic-tooltip', 'Kombi (Zielvereinbarung + Systematische Leistungsbewertung mit Unterkategorien)')} />
        <ReactTooltip id="bs-balanced_scorecard.tag.individual_and_systematic_with_sub_criteria-tooltip" content={translate('tooltips.employee.bs-balanced_scorecard.tag.individual_and_systematic-tooltip', 'Kombi (Zielvereinbarung + Systematische Leistungsbewertung mit Unterkategorien)')} />
        <ReactTooltip id="bs-balanced_scorecard.tag.individual_only-tooltip" content={translate('tooltips.employee.bs-balanced_scorecard.tag.individual_only-tooltip', 'Nur Zielvereinbarung')} />
        <ReactTooltip id="bs-balanced_scorecard.tag.systematic_only-tooltip" content={translate('tooltips.employee.bs-balanced_scorecard.tag.systematic_only-tooltip', 'Systematische Leistungsbewertung (ohne Unterkategorien)')} />
        <ReactTooltip id="bs-balanced_scorecard.tag.systematic_with_sub_criteria-tooltip" content={translate('tooltips.employee.bs-balanced_scorecard.tag.systematic_with_sub_criteria-tooltip', 'Systematische Leistungsbewertung (mit Unterkategorien)')} />
        <ReactTooltip id="bs-balanced_scorecard-template-locked-tooltip" content={translate('tooltips.employee.bs-balanced_scorecard-template-locked-tooltip', 'Die Vorlage der Leistungskarte kann nicht mehr geändert werden, da bereits Anpassungen an der zugehörigen Leistungskarte vorgenommen wurden. Wenn eine Änderung der Vorlage erforderlich ist, muss die bestehende Leistungskarte gelöscht und eine neue erstellt werden.')} />
        <ReactTooltip id="bs-balanced_scorecard-template-locked-db-tooltip" content={translate('tooltips.employee.bs-balanced_scorecard-template-locked-db-tooltip', 'Die Vorlage der Beurteilung kann nicht mehr geändert werden, da bereits Anpassungen an der zugehörigen Beurteilung vorgenommen wurden. Wenn eine Änderung der Vorlage erforderlich ist, muss die bestehende Beurteilung gelöscht und eine neue erstellt werden.')} />
        <ReactTooltip id="lob-module-tooltip" content={editableTranslate('tooltips.navbar.lob_module_tooltip', 'LOB')} />
        <ReactTooltip id="db-module-tooltip" content={editableTranslate('tooltips.navbar.db_module_tooltip', 'DB')} />
        <ReactTooltip id="state-interview-required-tooltip" content={translate('tooltips.employee.bs-balanced_scorecard.state_interview_required_tooltip')} place="bottom" />
        <ReactTooltip id="state-severe-disability-equality-tooltip" content={translate('tooltips.employee.severe-disability-equality-tooltip')} place="bottom" />

        <ReactTooltip id="form-save-tooltip" content={translate('tooltips.form.save')} place="bottom" />
        <ReactTooltip id="form-cancel-tooltip" content={translate('tooltips.form.cancel')} place="bottom" />

        <ReactTooltip id="setting-name-tooltip" place="bottom" />

        <ReactTooltip id="text-overflow-tooltip" place="bottom" />
      </div>
    </>
  )
}

function ConditionalHotjar({ userHashId, tenant, setHjl, setHjWsConnected }) {
  useEffect(() => {
    if (!process.env.REACT_APP_HOTJAR_ID || !userHashId || !tenant) return;

    const hotjarId = process.env.REACT_APP_HOTJAR_ID;

    // Step 1: Patch WebSocket to detect Hotjar connection
    const OriginalWebSocket = window.WebSocket;

    function PatchedWebSocket(url, protocols) {
      const ws = protocols
        ? new OriginalWebSocket(url, protocols)
        : new OriginalWebSocket(url);

      if (typeof url === 'string' && url.includes('ws.hotjar.com')) {
        ws.addEventListener('open', () => setHjWsConnected?.(true));
        ws.addEventListener('close', () => setHjWsConnected?.(false));
      }

      return ws;
    }

    window.WebSocket = PatchedWebSocket;
    PatchedWebSocket.prototype = OriginalWebSocket.prototype;

    // Step 2: Create Hotjar config script
    const configScript = document.createElement('script');
    configScript.innerHTML = `
      (function(h,o,t,j,a,r){
        h.hj = h.hj || function(){(h.hj.q = h.hj.q || []).push(arguments)};
        h._hjSettings={hjid:${hotjarId},hjsv:6};
        a=o.getElementsByTagName('head')[0];
        r=o.createElement('script');r.async=1;
        r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
        r.onload = function() {
          window.hj('identify', '${userHashId}', { tenant: '${tenant}' });
          window.dispatchEvent(new Event('hotjarLoaded'));
        };
        a.appendChild(r);
      })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
    `;

    document.head.appendChild(configScript);

    // Step 3: Listen for when Hotjar is fully loaded
    const handleLoaded = () => setHjl(true);
    window.addEventListener('hotjarLoaded', handleLoaded);

    return () => {
      window.removeEventListener('hotjarLoaded', handleLoaded);
      document.head.removeChild(configScript);
      window.WebSocket = OriginalWebSocket; // Restore original WebSocket
    };
  }, [userHashId, tenant, setHjl, setHjWsConnected]);

  return null;
}
